import { Alert, Box, Button, Container, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import WorkspaceLogo from '~/components/Logo/WorkspaceLogo';
import SupportEmailLink from '~/components/SupportEmailLink';
import { useAuthSession } from '~/contexts/auth/AuthSessionContext';
import { ACCESS_RIGHTS_QUERY_KEY } from '~/hooks/workflows/backend/useBackend';
import AppConfig from '~/utils/appconfig';

/**
 * Why the access check let the user no further:
 * - `refused`: the backend refused the account itself: it is not linked to an
 *   MVision user.
 * - `unlicensed`: the account is known, but holds no licence for this product.
 * - `unavailable`: the check produced no answer about the account — it went
 *   unanswered, was sent without a usable sign-in, or could not be sent — so
 *   nothing is known either way.
 */
export type AccessDeniedReason = 'refused' | 'unlicensed' | 'unavailable';

/** The navigation state this page is opened with. */
export interface AccessDeniedState {
    reason?: AccessDeniedReason;
    /** The route the user was opening, returned to on a retry. */
    from?: { pathname: string; search: string; hash: string };
}

const Title = ({ reason }: { reason: AccessDeniedReason }) => {
    switch (reason) {
        case 'unlicensed':
            return (
                <FormattedMessage
                    id="accessDenied.unlicensed.title"
                    defaultMessage="No Workspace+ licence"
                />
            );
        case 'unavailable':
            return (
                <FormattedMessage
                    id="accessDenied.unavailable.title"
                    defaultMessage="Your access could not be checked"
                />
            );
        default:
            return (
                <FormattedMessage
                    id="accessDenied.refused.title"
                    defaultMessage="This account cannot open Workspace+"
                />
            );
    }
};

const Explanation = ({ reason }: { reason: AccessDeniedReason }) => {
    switch (reason) {
        case 'unlicensed':
            return (
                <FormattedMessage
                    id="accessDenied.unlicensed.body"
                    defaultMessage="You are signed in, but this account has no Workspace+ licence. Ask your administrator for access, or sign in with a different account."
                />
            );
        case 'unavailable':
            return (
                <FormattedMessage
                    id="accessDenied.unavailable.body"
                    defaultMessage="Workspace+ could not confirm your access just now. Try again in a moment."
                />
            );
        default:
            return (
                <FormattedMessage
                    id="accessDenied.refused.body"
                    defaultMessage="The account you are signed in with is not linked to an MVision user. If you have another account, sign in with that one."
                />
            );
    }
};

/**
 * Tells the user that the access check did not let them in, and why, naming
 * the account they are signed in with — which the provider may have chosen
 * without asking, from a session they no longer remember holding.
 *
 * Offers the two ways forward: signing in with a different account, which asks
 * the provider for its account chooser, and checking again, which repeats the
 * access check for the route they were opening. Neither leaves them on a page
 * they cannot get past. Signing out ends the named session, which matters on
 * a shared workstation.
 */
const AccessDeniedPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { userName, userSignInName, switchAccount, logOut } =
        useAuthSession();
    const [switchFailed, setSwitchFailed] = useState(false);

    const state = (location.state ?? {}) as AccessDeniedState;
    // Without a reason no check ran, so nothing is known about the account.
    const reason: AccessDeniedReason = state.reason ?? 'unavailable';
    const account =
        userName && userSignInName && userName !== userSignInName
            ? `${userName} (${userSignInName})`
            : (userName ?? userSignInName);

    const tryAgain = () => {
        // The failed answer is cached; without dropping it the route would
        // read it back instead of asking again.
        queryClient.removeQueries({ queryKey: ACCESS_RIGHTS_QUERY_KEY });
        navigate(state.from ?? '/', { replace: true });
    };

    const signInWithAnotherAccount = () => {
        setSwitchFailed(false);
        // Resolves only as the browser leaves for the provider, so a
        // rejection means the redirect never started.
        switchAccount().catch(() => setSwitchFailed(true));
    };

    const switchButton = (variant: 'contained' | 'outlined'): ReactNode => (
        <Button variant={variant} fullWidth onClick={signInWithAnotherAccount}>
            <FormattedMessage
                id="accessDenied.switchAccount"
                defaultMessage="Sign in with a different account"
            />
        </Button>
    );
    const retryButton = (variant: 'contained' | 'outlined'): ReactNode => (
        <Button variant={variant} fullWidth onClick={tryAgain}>
            <FormattedMessage
                id="accessDenied.tryAgain"
                defaultMessage="Try again"
            />
        </Button>
    );

    return (
        <Container maxWidth="sm">
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                textAlign="center"
                gap={3}
            >
                <WorkspaceLogo height={120} />

                <Typography variant="h6" component="h1">
                    <Title reason={reason} />
                </Typography>

                {account && (
                    <Typography variant="body1">
                        <FormattedMessage
                            id="accessDenied.signedInAs"
                            defaultMessage="Signed in as {account}"
                            values={{ account: <strong>{account}</strong> }}
                        />
                    </Typography>
                )}

                <Typography variant="body2" color="text.secondary">
                    <Explanation reason={reason} />
                </Typography>

                <Box
                    display="flex"
                    flexDirection="column"
                    gap={1.5}
                    width="100%"
                    maxWidth={320}
                >
                    {/* The likelier way forward goes first: a refusal is about
                        the account, an unanswered check is about the moment. */}
                    {reason === 'unavailable' ? (
                        <>
                            {retryButton('contained')}
                            {switchButton('outlined')}
                        </>
                    ) : (
                        <>
                            {switchButton('contained')}
                            {retryButton('outlined')}
                        </>
                    )}
                    <Button variant="text" fullWidth onClick={() => logOut()}>
                        <FormattedMessage id="logout" defaultMessage="Logout" />
                    </Button>
                </Box>

                {switchFailed && (
                    <Alert severity="error" sx={{ maxWidth: 400 }}>
                        <FormattedMessage
                            id="accessDenied.switchFailed"
                            defaultMessage="The sign-in page could not be opened. Please try again."
                        />
                    </Alert>
                )}

                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                        id="accessDenied.support"
                        defaultMessage="If the problem persists, contact {email}"
                        values={{ email: <SupportEmailLink /> }}
                    />
                </Typography>

                <Typography
                    variant="caption"
                    display="block"
                    sx={{ opacity: 0.7 }}
                >
                    <FormattedMessage
                        id="versionInfo"
                        defaultMessage="Version {version}"
                        values={{ version: AppConfig.version }}
                    />
                </Typography>
            </Box>
        </Container>
    );
};

export default AccessDeniedPage;
